/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { TroyIcon } from "components/common"
import { useContext } from "react"
import { QuarterLoaderUniversal } from "@duxton-web/icons"
import _isString from "lodash/isString"
import {
  BULK_UPLOAD_TYPE,
  BulkUploadContext,
  DRAWER_STATES,
} from "pages/menu/detail/bulkUpload/BulkUploadContext"
import { translate, useLocaleContext } from "common/utils/i18n"
import { useSelector } from "react-redux"
import { isOngoingTaskFoundSelector } from "store/duck/bulkUpload/utils"

type PropTypes = {
  selectedFileName?: string
}

type UseTranslationTypes = {
  key: string
  replacement?: { [idx: string]: string }
}

const drawerBodyWidget = css`
  min-height: 180px;
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  background: #f5f5f5;
  border: 2px dashed #ebebeb;
  border-radius: 8px;
  cursor: pointer;
`

const fileIconCss = css`
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const fileIconPublishableCss = css`
  width: 64px;
`

const loadingIconWrapCss = css`
  position: relative;
`

const loadingIconCss = css`
  position: absolute;
  top: -17px;
  left: -20px;

  span {
    font-size: 90px;
    color: #00b14f;
  }
`

const FILE_WIDGET_CONTENT_SET: {
  [key in DRAWER_STATES]?: {
    fileIcon: JSX.Element
    title: (
      title?: string,
      actionType?: BULK_UPLOAD_TYPE,
    ) => UseTranslationTypes | string
    description: (desc?: string) => UseTranslationTypes | undefined
  }
} = {
  [DRAWER_STATES.START]: {
    fileIcon: <TroyIcon type="uploadGreen" />,
    title: (title: string, actionType: BULK_UPLOAD_TYPE) => ({
      key: `bulkUpload.drawers.start.dragNDropFileDescription_${actionType?.toLocaleLowerCase()}`,
    }),
    description: () => ({
      key: "bulkUpload.drawers.start.selectFileDescription",
    }),
  },
  [DRAWER_STATES.PUBLISHABLE]: {
    fileIcon: <TroyIcon type="publishableFile" />,
    title: (fileName: string) => fileName,
    description: () => ({
      key: "bulkUpload.drawers.publishable.replaceFileDescription",
    }),
  },
  [DRAWER_STATES.PUBLISHING]: {
    fileIcon: (
      <div css={loadingIconWrapCss}>
        <TroyIcon type="publishingFile" />
        <div css={loadingIconCss}>
          <QuarterLoaderUniversal />
        </div>
      </div>
    ),
    title: (fileName: string) => ({
      key: "bulkUpload.drawers.publishing.publishingFile",
      replacement: { "<filename>": fileName },
    }),
    description: () => undefined,
  },
  [DRAWER_STATES.PUBLISHING_ERROR_CHECK]: {
    fileIcon: (
      <div css={loadingIconWrapCss}>
        <TroyIcon type="publishingCheckingFile" />
        <div css={loadingIconCss}>
          <QuarterLoaderUniversal />
        </div>
      </div>
    ),
    title: () => ({
      key: "bulkUpload.drawers.publishingErrorCheck.checkingError",
    }),
    description: () => undefined,
  },
}

const FileWidget = ({ selectedFileName }: PropTypes) => {
  const { drawerState, bulkUploadType } = useContext(BulkUploadContext)
  const localeContent = useLocaleContext("localeContent")
  const isPublishable = drawerState === DRAWER_STATES.PUBLISHABLE

  // suppose to have maximum 1 ongoing task
  const isOngoingTaskFound = useSelector(isOngoingTaskFoundSelector)

  const contentSet = isOngoingTaskFound
    ? FILE_WIDGET_CONTENT_SET[DRAWER_STATES.PUBLISHING_ERROR_CHECK]
    : FILE_WIDGET_CONTENT_SET[drawerState]
  if (!contentSet) return null
  const { fileIcon, title, description } = contentSet

  const titleParam = title(selectedFileName, bulkUploadType)
  const displayedTitle = _isString(titleParam)
    ? titleParam
    : translate(localeContent, titleParam.key, titleParam.replacement)

  const i18nDescParams = description(selectedFileName)
  const displayedDesc = i18nDescParams
    ? translate(localeContent, i18nDescParams.key, i18nDescParams.replacement)
    : ""

  return (
    <div css={drawerBodyWidget}>
      <div
        css={css`
          ${fileIconCss}
          ${isPublishable ? fileIconPublishableCss : ""}
        `}
      >
        {fileIcon}
      </div>
      <div
        css={css`
          font-size: 16px;
          color: #303d55;
          margin-top: 16px;
          word-break: break-all;
          padding: 0 20px;
          text-align: center;
        `}
      >
        {displayedTitle}
      </div>
      {displayedDesc && (
        <div
          css={css`
            font-size: 14px;
            color: #a3a3a3;
            font-weight: normal;
          `}
        >
          {displayedDesc}
        </div>
      )}
    </div>
  )
}

export default FileWidget
